import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from '../../Services/firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    const number = parseFloat(value);
    if (isNaN(number)) return value.toString();
    return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const MaintenancePage = () => {
    const [tableData, setTableData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        costCenter: '',
        unit: '',
        quantity: '',
        unitCost: '',
    });
    const [editIndex, setEditIndex] = useState(null);
    const [user, setUser] = useState(null);
    const [kibaloName, setKibaloName] = useState('');
    const [basisOfCosting, setBasisOfCosting] = useState('');
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const { kibaloId } = useParams();
    const cleanKibaloId = kibaloId ? kibaloId.split('/')[0] : kibaloId;

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (authUser) => setUser(authUser));
        return () => unsubscribe();
    }, []);

    const fetchData = useCallback(async () => {
        if (!user || !cleanKibaloId) return;
        setLoading(true);

        const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
        const kibaloSnap = await getDoc(kibaloRef);

        if (kibaloSnap.exists()) {
            const data = kibaloSnap.data();
            setKibaloName(data.name || '');
            setTableData(data.maintenanceTable || []);

            // Fetch basis of costing
            const costingBasis = data.costingBasis;
            if (costingBasis) {
                const basisText = costingBasis.costBasis === 'year'
                    ? `Production per year: ${formatNumberWithCommas(costingBasis.quantity)} ${costingBasis.unit}`
                    : `Production per acreage: (${formatNumberWithCommas(costingBasis.quantity)} acres, ${formatNumberWithCommas(costingBasis.productionPerAcre)} ${costingBasis.unit}/year)`;
                setBasisOfCosting(basisText);
            }
        }

        setLoading(false);
    }, [user, cleanKibaloId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleModalSubmit = async () => {
        if (!user || !cleanKibaloId) return;

        const { costCenter, unit, quantity, unitCost } = formData;
        if (costCenter && unit && quantity && unitCost) {
            setSaving(true);
            const amount = (Number(quantity) * Number(unitCost)).toFixed(2);
            const newRow = { costCenter, unit, quantity, unitCost, amount };

            const updatedTableData = editIndex !== null
                ? tableData.map((row, idx) => idx === editIndex ? newRow : row)
                : [...tableData, newRow];

            const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
            try {
                await updateDoc(kibaloRef, { maintenanceTable: updatedTableData });
                setTableData(updatedTableData);
                setShowModal(false);
                setEditIndex(null);
                setFormData({ costCenter: '', unit: '', quantity: '', unitCost: '' });
            } catch (error) {
                console.error('Error saving maintenance data:', error);
                alert('Failed to save data. Please try again.');
            } finally {
                setSaving(false);
            }
        }
    };

    const handleEditRow = (index) => {
        setFormData(tableData[index]);
        setEditIndex(index);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ costCenter: '', unit: '', quantity: '', unitCost: '' });
        setEditIndex(null);
    };

    const handleDeleteRow = async (index) => {
        setItemToDelete(index);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete === null || !user || !cleanKibaloId) return;
        const updatedTableData = tableData.filter((_, i) => i !== itemToDelete);
        const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
        await updateDoc(kibaloRef, { maintenanceTable: updatedTableData });
        setTableData(updatedTableData);
        setShowDeleteModal(false);
        setItemToDelete(null);
    };


    if (loading) {
        return (
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6 animate-pulse">
                    <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                    <div className="space-y-3">
                        <div className="h-4 bg-gray-300 rounded"></div>
                        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-300 rounded w-4/6"></div>
                    </div>
                </div>
            </div>
        );
    }

    const calculateTotal = () =>
        tableData.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0).toFixed(2);

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6">

                {/* Title */}
                <h1 className="text-2xl font-bold text-gray-800 uppercase">
                 OTHER ANNUAL FIXED COSTS FOR {kibaloName} ENTERPRISE
                </h1>

                {/* Basis of Costing */}
                <p className="text-md font-semibold text-gray-600">{basisOfCosting}</p>

                {/* Table */}
                <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold">
                            {['Cost Center', 'Unit', 'Quantity', 'Unit Cost', 'Amount', 'Actions'].map(title => (
                                <th key={title} className="border px-4 py-3 text-left">{title}</th>
                            ))}
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {tableData.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center py-6 text-gray-500">
                                        No Other annual fixed costs added yet.
                                    </td>
                                </tr>
                            )}
                            {tableData.map((row, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition">
                                    <td className="border px-4 py-3">{row.costCenter}</td>
                                    <td className="border px-4 py-3">{row.unit}</td>
                                    <td className="border px-4 py-3">{formatNumberWithCommas(row.quantity)}</td>
                                    <td className="border px-4 py-3">{formatNumberWithCommas(row.unitCost)}</td>
                                    <td className="border px-4 py-3">{formatNumberWithCommas(row.amount)}</td>
                                    <td className="border px-4 py-3 flex gap-2 justify-center">
                                        <button
                                            onClick={() => handleEditRow(index)}
                                            className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md text-xs"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteRow(index)}
                                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            <tr className="bg-gray-100 font-semibold">
                                <td colSpan="4" className="border px-4 py-3 text-right">Total</td>
                                <td className="border px-4 py-3">{formatNumberWithCommas(calculateTotal())}</td>
                                <td className="border px-4 py-3"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Bottom Buttons */}
                <div className="mt-4 flex justify-between gap-2">
                    <button
                        onClick={() => navigate(`/equipment-costing/${cleanKibaloId}`)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg shadow text-sm font-medium"
                    >
                        Back to Depreciation
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow text-sm font-medium"
                    >
                        Add Annual fixed Cost
                    </button>
                    <button
                        onClick={() => navigate(`/interest-costing/${cleanKibaloId}`)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow text-sm font-medium"
                    >
                        Go to Interest
                    </button>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50">
                    <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl">
                        <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                            {editIndex !== null ? 'Edit Maintenance Cost' : 'Add Maintenance Cost'}
                        </h2>

                        <div className="space-y-3">
                            {['costCenter', 'unit', 'quantity', 'unitCost'].map(field => (
                                <div key={field}>
                                    <label className="block text-sm font-medium text-gray-700 capitalize">{field}</label>
                                    <input
                                        type={['quantity', 'unitCost'].includes(field) ? 'number' : 'text'}
                                        value={formData[field]}
                                        onChange={e => setFormData({ ...formData, [field]: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        disabled={saving}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg shadow text-sm"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleModalSubmit}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow text-sm"
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : editIndex !== null ? 'Save Changes' : 'Add Cost'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-1/3">
                        <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
                        <p>Are you sure you want to delete this item?</p>
                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md shadow hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded-md shadow hover:bg-red-600">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaintenancePage;
